import {
  Users,
  ListChecks,
  Receipt,
  GitBranch,
  UserPlus,
  Plus,
  RefreshCw,
  ArrowUpRight,
  TrendingUp,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import AnimatedCounter from "@/components/AnimatedCounter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { quickActions, snapshotSeries } from "@/data/mockData";
import { useData } from "@/context/DataContext";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const ICONS = {
  users: Users,
  "list-checks": ListChecks,
  receipt: Receipt,
  "git-branch": GitBranch,
  "user-plus": UserPlus,
  plus: Plus,
  "refresh-cw": RefreshCw,
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { clients, tasks, orders } = useData();

  const activeClients = clients.filter((c) => c.status === "Active").length;
  const pendingTasks = tasks.filter((t) => !t.done && t.status !== "Completed").length;
  const openOrders = orders.filter((o) => o.status !== "Completed").length;
  const overviewStats = [
    {
      key: "clients",
      label: "Active Clients",
      value: activeClients,
      change: `${clients.length} total in portal`,
      icon: "users",
    },
    {
      key: "tasks",
      label: "Pending Tasks",
      value: pendingTasks,
      change: `${tasks.filter((t) => t.due === "Due Today").length} due today`,
      icon: "list-checks",
    },
    {
      key: "orders",
      label: "Open Orders",
      value: openOrders,
      change: `${orders.filter((o) => o.status === "Pending Payment").length} awaiting payment`,
      icon: "receipt",
    },
    {
      key: "workflow",
      label: "Workflow Status",
      value: "In Progress",
      change: "4 stages active",
      icon: "git-branch",
    },
  ];

  const handleAction = (action) => {
    if (action.key === "add-client") navigate("/clients?new=1");
    else if (action.key === "create-task") navigate("/tasks?new=1");
    else if (action.key === "update-order") navigate("/orders?new=1");
    else if (action.key === "view-workflow") navigate("/workflow");
  };

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <PageHeader
        title="Welcome to Your Business Command Center"
        subtitle="Your operations, clients, tasks, and workflow — all in one clean, organized dashboard."
      />

      {/* Overview */}
      <section data-testid="quick-overview">
        <div className="flex items-center gap-2 mb-4">
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
            Quick Overview
          </div>
          <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
          {overviewStats.map((stat, idx) => {
            const Icon = ICONS[stat.icon] || Users;
            return (
              <Card
                key={stat.key}
                data-testid={`stat-${stat.key}`}
                style={{ ["--i"]: idx }}
                className="p-6 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl elev-1 tilt"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.15em]">
                      {stat.label}
                    </div>
                    <div className="mt-2 font-display text-3xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                      <AnimatedCounter value={stat.value} testid={`stat-value-${stat.key}`} />
                    </div>
                    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{stat.change}</div>
                  </div>
                  <div className="h-9 w-9 rounded-lg bg-primary/10 dark:bg-primary/15 text-primary flex items-center justify-center sheen">
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Quick Actions */}
      <section data-testid="quick-actions">
        <div className="flex items-center gap-2 mb-4">
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
            Quick Actions
          </div>
          <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 stagger">
          {quickActions.map((action, idx) => {
            const Icon = ICONS[action.icon] || Plus;
            return (
              <Button
                key={action.key}
                data-testid={`quick-action-${action.key}`}
                variant="outline"
                onClick={() => handleAction(action)}
                style={{ ["--i"]: idx + 4 }}
                className="h-auto justify-start gap-3 px-4 py-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-xl elev-1 lift hover:border-primary/40 dark:border-blue-900 hover:bg-primary/10 dark:hover:bg-primary/15 dark:bg-blue-950/60/30 dark:bg-blue-950/30 group"
              >
                <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0 transition-transform group-hover:scale-110 sheen">
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <div className="text-left min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                    {action.label}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">Get it done</div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-slate-400 dark:text-slate-500 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              </Button>
            );
          })}
        </div>
      </section>

      {/* Business Snapshot */}
      <section data-testid="business-snapshot">
        <div className="flex items-center gap-2 mb-4">
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
            Business Snapshot
          </div>
          <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
        </div>
        <Card className="p-6 md:p-8 border border-slate-200 dark:border-slate-800 rounded-xl elev-1">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-100">
                Your business activity at a glance.
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Stay organized, stay efficient, and stay in control.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-primary" />
                <span className="text-xs text-slate-600 dark:text-slate-300">Clients</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-slate-600" />
                <span className="text-xs text-slate-600 dark:text-slate-300">Orders</span>
              </div>
              <div className="hidden md:flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300 font-medium ml-2">
                <TrendingUp className="h-3.5 w-3.5" /> +24% YoY
              </div>
            </div>
          </div>

          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={snapshotSeries} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="clientsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="ordersGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#475569" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#475569" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" stroke="#94a3b8" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    background: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="clients"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2.25}
                  fill="url(#clientsGrad)"
                  isAnimationActive
                  animationDuration={1200}
                  animationEasing="ease-out"
                  activeDot={{ r: 5, strokeWidth: 2, fill: "#fff", stroke: "hsl(var(--primary))" }}
                />
                <Area
                  type="monotone"
                  dataKey="orders"
                  stroke="#475569"
                  strokeWidth={2}
                  fill="url(#ordersGrad)"
                  isAnimationActive
                  animationDuration={1400}
                  animationBegin={200}
                  animationEasing="ease-out"
                  activeDot={{ r: 4, strokeWidth: 2, fill: "#fff", stroke: "#475569" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </section>
    </div>
  );
}
